/**
 * composeWrapupView — the single place TEN-2402 is fixed on the MCP surface.
 *
 * The kernel `getSessionWrapup` returns capped `committed`/`drafts` (≤15 each, with
 * per-entry enrichment) PLUS slim overflow lists beyond the cap: `overflowCommitted`,
 * `overflowDrafts` (each `{ entryId, name, docId }`), and a positional `overflowUnscanned`
 * count for entries past the scan cap that were never read.
 *
 * This helper MERGES the capped and overflow lists so every surface names the FULL set:
 *   committed = committed ∪ overflowCommitted
 *   drafts    = drafts ∪ overflowDrafts
 *
 * The merge MUST live here (not at the call site) so the repo-level parity test can pin it
 * against the CLI's twin helper. `canClose` is the REVIEW predicate (the loop is empty);
 * the commit-all path AND-s its own post-commit `failed === 0` fact at the call site,
 * NOT here (that fact is absent from the raw kernel result).
 *
 * Every read of an overflow field coalesces `?? []` / `?? 0` so a cross-deploy cache object
 * predating the field shape can never throw (Staff+ MAJOR-1).
 */

/** Minimal entry shape every surface can name. The capped entries carry more; the merge keeps it. */
export interface ComposedEntry {
  entryId: string;
  name: string;
  docId: string;
}

/** Slim overflow element from the kernel. */
interface OverflowEntry {
  entryId: string;
  name: string;
  docId: string;
}

/** The raw kernel wrapup result this helper consumes (only the fields it reads are required). */
export interface RawWrapupView {
  committed?: Array<{ entryId: string; name: string; docId: string }>;
  drafts?: Array<{ entryId: string; name: string; docId: string }>;
  overflowCommitted?: OverflowEntry[];
  overflowDrafts?: OverflowEntry[];
  overflowUnscanned?: number;
}

export interface ComposedWrapupView {
  committed: ComposedEntry[];
  drafts: ComposedEntry[];
  overflowUnscanned: number;
  /** REVIEW predicate: the loop is empty. Commit-all AND-s `failed === 0` at the call site. */
  canClose: boolean;
}

function toComposed(e: { entryId: string; name: string; docId: string }): ComposedEntry {
  return { entryId: e.entryId, name: e.name, docId: e.docId };
}

export function composeWrapupView(raw: RawWrapupView): ComposedWrapupView {
  const committed = [
    ...(raw.committed ?? []).map(toComposed),
    ...(raw.overflowCommitted ?? []).map(toComposed),
  ];
  const drafts = [
    ...(raw.drafts ?? []).map(toComposed),
    ...(raw.overflowDrafts ?? []).map(toComposed),
  ];
  const overflowUnscanned = raw.overflowUnscanned ?? 0;
  return {
    committed,
    drafts,
    overflowUnscanned,
    canClose: drafts.length === 0 && overflowUnscanned === 0,
  };
}
